import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, Alert, CircularProgress, Chip
} from "@mui/material"
import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { pausePayouts, resumePayouts } from "../../redux/admin-slices/wallet.slice"

/**
 * PauseResumeDialog
 *
 * Pause or Resume payouts for a specialist.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   mode       — "pause" | "resume"
 *   specialist — { _id, fullName, email, payoutPaused, payoutPausedReason }
 *   onSuccess  — () => void
 */
export default function PauseResumeDialog({ open, onClose, mode = "pause", specialist, onSuccess }) {
  const dispatch   = useDispatch()
  const [reason, setReason]   = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState("")

  useEffect(() => {
    if (open) { setReason(""); setError("") }
  }, [open])

  const isPause = mode === "pause"

  const handleSubmit = async () => {
    if (isPause && reason.trim().length < 10) {
      setError("Please provide a reason (min 10 characters).")
      return
    }
    setLoading(true)
    try {
      const action = isPause
        ? pausePayouts({ specialistId: specialist._id, reason: reason.trim() })
        : resumePayouts({ specialistId: specialist._id })
      const result = await dispatch(action).unwrap()
      if (result) { onSuccess?.(); onClose() }
    } catch {
      // toast shown by thunk
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>
        {isPause ? "⏸ Pause Payouts" : "▶ Resume Payouts"}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 2 }}>
          <Typography fontWeight={600} textTransform="capitalize">{specialist?.fullName}</Typography>
          <Typography color="text.secondary" fontSize={13}>{specialist?.email}</Typography>
        </Box>

        {isPause ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Pausing payouts will <strong>block all future payout attempts</strong> for this specialist until you resume.
            </Alert>
            <TextField
              label="Reason *"
              fullWidth multiline rows={3}
              value={reason}
              onChange={(e) => { setReason(e.target.value); setError("") }}
              error={Boolean(error)}
              helperText={error || "Min 10 characters — this will be stored against the wallet."}
              inputProps={{ maxLength: 300 }}
            />
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This will re-enable payouts for this specialist. Ensure any dispute or fraud concern has been resolved.
            </Alert>
            {specialist?.payoutPausedReason && (
              <Box sx={{ p: 1.5, bgcolor: "#fff8e1", borderRadius: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  PAUSE REASON:
                </Typography>
                <Typography fontSize={14}>{specialist.payoutPausedReason}</Typography>
              </Box>
            )}
            {specialist?.flaggedForReview && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <strong>Note:</strong> This account is still <strong>flagged for review</strong>.
                Resuming payouts here will not clear the flag —
                the backend will still block payouts until you also unflag the account.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">Cancel</Button>
        <Button
          variant="contained"
          color={isPause ? "warning" : "success"}
          disabled={loading}
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ minWidth: 120 }}
        >
          {loading ? "Saving…" : isPause ? "Pause Payouts" : "Resume Payouts"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
